<template>
    <NewDeliveryRequest
        v-if="loaded"
        :receipt-logs="receiptLogs"
        :show="show"
        :has-past-due-invoices="!!numberPastDueInvoices"
        :locked-out="lockedOut"
        :is-will-call="isWillCall"
        @update:show="$emit('update:show', $event)"
        @addDeliveryRequest="addNewDeliveryRequest"
        @cancel="$emit('cancel')"
        @showPastDueInvoicesMessage="showPastDueInvoicesMessage"
    />
</template>
<script>
import SmartyStreetsSDK from "smartystreets-javascript-sdk";
import { mapActions, mapState } from "vuex";
import swal from "sweetalert2";
import store from "./_store";
import NewDeliveryRequest from "./_components/NewDeliveryRequest.vue";

const SmartyStreetsCore = SmartyStreetsSDK.core;
const { Lookup } = SmartyStreetsSDK.usStreet;

const credentials = new SmartyStreetsCore.SharedCredentials(
    process.env.VUE_APP_SMARTY_WEBSITE_KEY,
);
const usStreetClientBuilder = new SmartyStreetsCore.ClientBuilder(credentials);
console.log(usStreetClientBuilder);
const streetClient = usStreetClientBuilder.buildUsStreetApiClient();

export default {
    name: "NewDeliveryRequestModule",
    components: { NewDeliveryRequest },
    props: {
        receiptLogs: {
            type: Array,
            required: true,
        },
        show: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            pastDueInvoiceMessage: "",
            numberPastDueInvoices: 0,
            daysPastDue: 0,
            loaded: false,
            lockedOut: false,
        };
    },
    computed: {
        ...mapState("deliveryRequest", ["requestDeliveryType"]),
        isWillCall() {
            return this.requestDeliveryType === "willCall";
        },
    },
    beforeCreate() {
        this.$store.registerModule("$_deliveryRequest", store);
    },
    destroyed() {
        this.$store.unregisterModule("$_deliveryRequest");
    },
    async created() {
        const info = await this.getPastDueInvoiceInfo();
        this.pastDueInvoiceMessage = info.message;
        this.numberPastDueInvoices = info.count;
        this.daysPastDue = info.daysPastDue;
        this.loaded = true;

        if (info.terms === 30) {
            if (info.daysPastDue >= 35) {
                this.lockedOut = true;
            }
        }
        if (info.terms === 15) {
            if (info.daysPastDue >= 20) {
                this.lockedOut = true;
            }
        }
    },
    methods: {
        ...mapActions("$_deliveryRequest", [
            "getPastDueInvoiceInfo",
            "addDeliveryRequest",
        ]),
        async addNewDeliveryRequest(deliveryRequest) {
            const lookup = new Lookup();
            let addressLookup;
            lookup.street = deliveryRequest.deliveryAddress;
            lookup.city = deliveryRequest.deliveryCity;
            lookup.zipCode = deliveryRequest.deliveryZip;
            lookup.state = "CO";
            lookup.match = "strict";
            let result;
            try {
                const response = await streetClient.send(lookup);
                addressLookup = response.lookups[0];
                result = addressLookup.result;
            } catch (error) {
                console.error(error);
            }

            let sendRequest = true;
            // there was a match
            if (result && result.length) {
                const candidate = result[0];
                if (
                    addressLookup.street !== candidate.deliveryLine1 ||
                    addressLookup.city !== candidate.components.cityName ||
                    addressLookup.zipCode !== candidate.components.zipCode
                ) {
                    // entered address is not the same as the candidate
                    const { value } = await swal({
                        title: "Delivery Address Verification",
                        allowEscapeKey: false,
                        allowOutsideClick: false,
                        showConfirmButton: true,
                        confirmButtonText: "Use verified address",
                        confirmButtonColor: "#3085d6",
                        showCancelButton: true,
                        cancelButtonText: "Use my original address",
                        cancelButtonColor: "#ff8d72",
                        type: "warning",
                        html: `
            <div class="row mb-3">
              <div class="col-12"><h3 class="mb-0">Original Address:</h3></div>
              <div class="col-12">${addressLookup.street}</div>
              <div class="col-12">${addressLookup.city}, ${addressLookup.state} ${addressLookup.zipCode}</div>
            </div>
            <div class="row">
              <div class="col-12"><h3 class="mb-0">Verified Address:</h3></div>
              <div class="col-12">${candidate.deliveryLine1}</div>
              <div class="col-12">${candidate.lastLine}</div>
            </div>
            `,
                    });
                    if (value) {
                        deliveryRequest.deliveryAddress =
                            candidate.deliveryLine1;
                        deliveryRequest.deliveryCity =
                            candidate.components.cityName;
                        deliveryRequest.deliveryZip =
                            candidate.components.zipCode;
                        deliveryRequest.deliveryAddressVerified = true;
                    } else {
                        deliveryRequest.deliveryAddressVerified = false;
                    }
                } else {
                    // perfect match
                    deliveryRequest.deliveryAddressVerified = true;
                }
            } else {
                const street = deliveryRequest.deliveryAddress;
                const city = deliveryRequest.deliveryCity;
                const state = "CO";
                const zipCode = deliveryRequest.deliveryZip;

                const { value } = await swal({
                    title: "Delivery Address Not Verified",
                    allowEscapeKey: false,
                    allowOutsideClick: false,
                    showConfirmButton: true,
                    confirmButtonText: "Use this address anyway",
                    confirmButtonColor: "#ec250d",
                    showCancelButton: true,
                    cancelButtonText: "Let me change the address",
                    cancelButtonColor: "#3085d6",
                    type: "error",
                    html: `
          <div class="col-12">${street}</div>
          <div class="col-12">${city}, ${state} ${zipCode}</div>
          `,
                });
                if (value) {
                    deliveryRequest.deliveryAddressVerified = false;
                } else {
                    sendRequest = false;
                }
            }

            if (sendRequest) {
                await this.addDeliveryRequest(deliveryRequest);
                this.$emit("update:show", false);
                const requestLabel = deliveryRequest.willCall
                    ? "will call"
                    : "delivery";
                swal({
                    title: "Success!",
                    text: `Your ${requestLabel} request has been received and a confirmation email was sent.`,
                    buttonsStyling: false,
                    confirmButtonClass: "btn btn-success btn-fill",
                    type: "success",
                });
            }
        },
        showPastDueInvoicesMessage() {
            const invoiceLabel =
                this.numberPastDueInvoices > 1 ? "Invoices" : "Invoice";
            swal({
                title: `${this.numberPastDueInvoices} Past Due ${invoiceLabel}`,
                text: this.pastDueInvoiceMessage,
                buttonsStyling: false,
                confirmButtonClass: "btn btn-danger btn-fill",
                type: "error",
            });
        },
    },
};
</script>
